import React from 'react';
import { Card, CardContent } from './ui/card';
import { Building2, User, Award, Leaf } from 'lucide-react';

const About = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-3 block">
                L'Entreprise
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                VIREO, votre entreprise de rénovation
              </h2>
              
              <div className="space-y-4 mb-8">
                <p className="text-lg text-slate-700 leading-relaxed">
                  VIREO est une <strong>entreprise de rénovation</strong> à Paris et en Île-de-France : rénovation complète, rénovation intérieure, rénovation énergétique, façade et isolation. Nous accompagnons nos clients de l'étude à la livraison, avec un interlocuteur unique.
                </p>

                <p className="text-lg text-slate-700 leading-relaxed">
                  Selon les projets, nous mettons en œuvre des <strong>matériaux étudiés</strong>, y compris des solutions naturelles et biosourcées (chaux, fibre de bois, chanvre) adaptées au bâti ancien comme récent.
                </p>
                
                <p className="text-lg text-slate-700 leading-relaxed">
                  Nous intervenons pour les particuliers, les copropriétés et les syndics, et travaillons avec les architectes, maîtres d'œuvre et professionnels de l'immobilier.
                </p>

                <p className="text-lg text-slate-700 leading-relaxed">
                  Notre priorité : la qualité d'exécution, le respect des règles de l'art et la satisfaction de nos clients.
                </p>
              </div>

              {/* Company Info Cards */}
              <div className="grid sm:grid-cols-1 gap-4">
                <Card className="border-slate-200 bg-slate-50">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Siège social</p>
                        <p className="font-semibold text-slate-900">29 rue Tronchet</p>
                        <p className="text-sm text-slate-600">75008 Paris</p>
                        <p className="text-sm text-slate-600 mt-1">Île-de-France</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="about-artisan-card" className="border-slate-200 bg-slate-50">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-center gap-4">
                      <img src="/images/logo-artisan.png" alt="Logo Artisan CMA" className="h-14 w-auto" />
                      <div>
                        <p className="text-sm text-slate-600">Qualification professionnelle</p>
                        <p className="font-semibold text-slate-900">Artisan — attestation officielle</p>
                        <p className="text-sm text-slate-600">Rénovation, isolation, peinture &amp; revêtements</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Content - Values */}
            <div>
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 md:p-10">
                <h3 className="text-2xl font-bold text-white mb-8">
                  Nos valeurs
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">
                        Excellence technique
                      </h4>
                      <p className="text-slate-300">
                        Respect strict des DTU et utilisation de matériaux certifiés pour garantir la qualité et la durabilité de nos interventions.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Leaf className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">
                        Engagement biosourcé
                      </h4>
                      <p className="text-slate-300">
                        Matériaux biosourcés et naturels — chaux, fibre de bois, chanvre — pour une rénovation énergétique performante à faible impact carbone.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">
                        Relation client privilégiée
                      </h4>
                      <p className="text-slate-300">
                        Écoute, transparence et suivi personnalisé tout au long de votre projet de ravalement de façade.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">
                        Expertise locale
                      </h4>
                      <p className="text-slate-300">
                        Connaissance approfondie du patrimoine parisien et des spécificités architecturales locales.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
